import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

import logo_url from "../data/rhino.webp";

import { Button } from "@/components/ui/button";
import { GET_INVOICE_DETAIL } from "@/context/Api";

const Invoice = () => {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  /* =========================================
     GET INVOICE DETAILS
  ========================================= */

  useEffect(() => {
    const getInvoiceDetail = async () => {
      if (!token) {
        toast.error("Invoice token not found.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await GET_INVOICE_DETAIL(token);

        console.log("Invoice response:", response);

        if (response?.data?.data) {
          setInvoice(response.data.data);
        } else {
          toast.error("Invoice details not found.");
        }
      } catch (error) {
        console.error("GET_INVOICE_DETAIL error:", error);

        toast.error(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to fetch invoice details."
        );
      } finally {
        setLoading(false);
      }
    };

    getInvoiceDetail();
  }, [token]);

  /* =========================================
     FORMAT AMOUNT
  ========================================= */

  const formatAmount = (amount) => {
    if (
      amount === null ||
      amount === undefined ||
      amount === ""
    ) {
      return "0.00";
    }

    const number = Number(amount);

    if (Number.isNaN(number)) {
      return "0.00";
    }

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /* =========================================
     FORMAT DATE
  ========================================= */

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  /* =========================================
     INVOICE NUMBER
  ========================================= */

  const getInvoiceNumber = () => {
    return (
      invoice?.tax_invoice_number ||
      invoice?.invoice_number ||
      invoice?.invoice_no ||
      invoice?.id ||
      invoice?.order_id ||
      "Receipt"
    );
  };

  /* =========================================
     INVOICE VALUES

     API RESPONSE (as actually received):

     actual_amount = 4999.00   <-- ORIGINAL PRICE (MRP), BEFORE DISCOUNT
     coupon_row.discount = 1000 <-- DISCOUNT AMOUNT/PERCENTAGE (applied coupon)
     coupon_row.discount_type = "percentage" | "flat" (or similar)
     amount        = 3279.18   <-- TAXABLE VALUE (after discount, before tax)
     cgst_rate     = 359.91    <-- CGST AMOUNT
     sgst_rate     = 359.91    <-- SGST AMOUNT
     item_cgst_rate = "9%"     <-- CGST %
     item_sgst_rate = "9%"     <-- SGST %
     net_amount    = 3999.00   <-- FINAL TOTAL (actual_amount - discount)
  ========================================= */

  // Taxable / base amount (already net of discount, pre-tax)
  const taxableAmount = Number(
    invoice?.amount || 0
  );

  /*
    IMPORTANT FIX:

    The discount is NOT sent as a top-level `discount` /
    `item_discount` field. It actually lives inside the
    applied coupon object:

      invoice.coupon_row.discount

    Previously this always evaluated to 0 because none of
    the checked keys existed on the payload, so the
    "Discount" row on the invoice never showed anything.
  */

  // Coupon discount type: "percentage" means coupon_row.discount is a %
  // value (e.g. 20 => 20%), not a currency amount.
  const discountType = (
    invoice?.coupon_row?.discount_type || ""
  ).toLowerCase();

  // Raw value coming from the API. For percentage coupons this is the
  // percentage number (e.g. 20), for flat coupons this is the currency
  // amount (e.g. 1000).
  const discountRaw = Number(
    invoice?.discount ??
    invoice?.item_discount ??
    invoice?.coupon_row?.discount ??
    0
  );

  // Original price / MRP before discount was applied.
  const mrpAmount = Number(
    invoice?.actual_amount ??
    (discountType === "percentage"
      ? taxableAmount
      : taxableAmount + discountRaw)
  );

  // Actual discount amount in currency, used for totals/table math.
  // - percentage coupon: derive amount from the % against the MRP
  // - flat coupon: raw value is already the currency amount
  const discount =
    discountType === "percentage"
      ? Number(((mrpAmount * discountRaw) / 100).toFixed(2))
      : discountRaw;

  // Quantity
  const quantity = Number(
    invoice?.item_quantity ??
    invoice?.quantity ??
    1
  );

  const cgstAmount = Number(
    invoice?.item_cgst_amount ??
    invoice?.cgst_amount ??
    invoice?.cgst_rate ??
    0
  );

  const sgstAmount = Number(
    invoice?.item_sgst_amount ??
    invoice?.sgst_amount ??
    invoice?.sgst_rate ??
    0
  );

  // Actual CGST percentage
  const cgstRate =
    invoice?.item_cgst_rate ||
    invoice?.cgst_percentage ||
    "9%";

  // Actual SGST percentage
  const sgstRate =
    invoice?.item_sgst_rate ||
    invoice?.sgst_percentage ||
    "9%";

  /*
    Final payable amount.

    Backend:
    net_amount = 3999.00 (= actual_amount - discount)

    So this should be used for:
    - Net Amount
    - Total
  */

  const finalAmount = Number(
    invoice?.net_amount ??
    invoice?.item_net_amount ??
    taxableAmount + cgstAmount + sgstAmount
  );

  const currencySymbol =
    invoice?.currency === "USD" ? "$" : "₹";

  // Coupon code (for display next to the discount line, if present)
  const couponCode = invoice?.coupon_row?.coupon_code || "";

  /*
    DISCOUNT DISPLAY TEXT

    - Percentage coupon: show the raw % value with a trailing "%"
      (e.g. "20%") instead of formatting it as currency.
    - Flat/other coupon: keep showing it as a currency amount
      (e.g. "- ₹ 1,000.00").
  */
  const discountDisplay =
    discount > 0
      ? discountType === "percentage"
        ? `${discountRaw}%`
        : `- ${currencySymbol} ${formatAmount(discount)}`
      : `${currencySymbol} 0.00`;

  /* =========================================
     DOWNLOAD PDF
  ========================================= */

  const handleDownload = async () => {
    const element = document.getElementById(
      "invoice-receipt-card"
    );

    if (!element) {
      toast.error("Invoice not found.");
      return;
    }

    try {
      setDownloading(true);

      const cleanInvoiceNumber = String(
        getInvoiceNumber()
      ).replace(/[^a-zA-Z0-9_-]/g, "_");

      const filename = `Invoice_${cleanInvoiceNumber || "Receipt"
        }.pdf`;

      const options = {
        margin: [8, 8, 8, 8],

        filename,

        image: {
          type: "jpeg",
          quality: 0.98,
        },

        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
        },

        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },

        pagebreak: {
          mode: ["avoid-all", "css"],
        },
      };

      await html2pdf()
        .set(options)
        .from(element)
        .save();

      toast.success(
        "Invoice downloaded successfully."
      );
    } catch (error) {
      console.error(
        "PDF generation error:",
        error
      );

      toast.error(
        "Unable to download invoice."
      );
    } finally {
      setDownloading(false);
    }
  };

  /* =========================================
     LOADING
  ========================================= */

  if (loading) {
    return (
      <>
        <div className="invoice-loading-page">
          <div className="invoice-loader">
            <Loader2
              size={25}
              className="invoice-loader-icon"
            />

            <span>Loading invoice...</span>
          </div>
        </div>

        <style>{`
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
          }

          .invoice-loading-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8fafc;
            font-family:
              -apple-system,
              BlinkMacSystemFont,
              "Segoe UI",
              Roboto,
              Helvetica,
              Arial,
              sans-serif;
          }

          .invoice-loader {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #64748b;
            font-size: 14px;
          }

          .invoice-loader-icon {
            color: #0284c7;
            animation: invoice-spin 1s linear infinite;
          }

          @keyframes invoice-spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </>
    );
  }

  /* =========================================
     EMPTY STATE
  ========================================= */

  if (!invoice) {
    return (
      <>
        <div className="invoice-empty-page">
          <div className="invoice-empty-card">
            <div className="empty-icon">
              !
            </div>

            <h2>
              Invoice details not found
            </h2>

            <p>
              The invoice token may be invalid
              or expired.
            </p>
          </div>
        </div>

        <style>{`
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
          }

          .invoice-empty-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8fafc;
            padding: 20px;
            font-family:
              -apple-system,
              BlinkMacSystemFont,
              "Segoe UI",
              Roboto,
              Helvetica,
              Arial,
              sans-serif;
          }

          .invoice-empty-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 40px;
            text-align: center;
            box-shadow:
              0 5px 20px
              rgba(15, 23, 42, 0.05);
          }

          .empty-icon {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            margin: 0 auto 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fef2f2;
            color: #dc2626;
            font-size: 20px;
            font-weight: 800;
          }

          .invoice-empty-card h2 {
            margin: 0;
            color: #111827;
            font-size: 20px;
          }

          .invoice-empty-card p {
            margin: 8px 0 0;
            color: #64748b;
            font-size: 14px;
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      <div className="invoice-page">
        <main className="invoice-wrapper">

          {/* =========================================
              ACTION BAR
          ========================================= */}

          <div className="top-actions-bar no-print">

            <div className="status-badge">
              <span className="status-check">
                ✓
              </span>

              <span>
                Payment Completed
              </span>
            </div>

            <div className="action-buttons">
              <Button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="btn-download-pdf"
              >
                {downloading ? (
                  <Loader2
                    size={16}
                    className="download-loader"
                  />
                ) : (
                  <Download size={16} />
                )}

                {downloading
                  ? "Generating PDF..."
                  : "Download PDF"}
              </Button>
            </div>
          </div>

          {/* =========================================
              INVOICE CARD
          ========================================= */}

          <div
            id="invoice-receipt-card"
            className="invoice-card"
          >

            {/* =========================================
                HEADER
            ========================================= */}

            <div className="invoice-header">

              {/* LEFT */}

              <div className="header-brand">

                <img
                  src={
                    logo_url
                  }
                  alt="HackTonomy"
                  className="brand-logo"
                  crossOrigin="anonymous"
                />

                <span className="company-name">
                  HackTonomy
                </span>

              </div>

              {/* RIGHT */}

              <div className="header-meta">

                <div className="invoice-title">
                  TAX INVOICE
                </div>

                <div className="invoice-number">
                  Invoice No. :{" "}
                  <span>CFPL/HT/
                    {getInvoiceNumber()}
                  </span>
                </div>


                <div className="invoice-date">
                  Date:{" "}
                  {formatDate(
                    invoice?.invoice_date_time ||
                    invoice?.created_at
                  )}
                </div>

              </div>

            </div>

            {/* =========================================
                SELLER / BILLING
            ========================================= */}

            <div className="parties-grid">

              {/* SELLER */}

              <div className="party-col seller-col">

                <h4>
                  Sold By:
                </h4>

                <div className="seller-name">

                  CyberFrat Private Limited
                </div>

                <div className="seller-info">
                  GSTIN:{" "}
                  {invoice?.seller_gstin ||
                    "N/A"}
                </div>

                <div className="seller-info">
                  {invoice?.seller_address_line1 ||
                    "-"}
                </div>

                <div className="seller-info">
                  {invoice?.seller_address_line2 ||
                    "-"}
                </div>

              </div>

              {/* BILLING */}

              <div className="party-col billing-col">

                <h4>
                  Billing Details:
                </h4>

                <div className="billing-details">

                  <div className="billing-name">
                    <span className="billing-label">
                      Name:
                    </span> {" "}
                    {invoice?.customer_name ||
                      "-"}
                  </div>

                  <div className="billing-row">

                    <span className="billing-label">
                      Email:
                    </span>

                    <span className="billing-value">
                      {invoice?.customer_email ||
                        "-"}
                    </span>

                  </div>

                  <div className="billing-row">

                    <span className="billing-label">
                      Phone:
                    </span>

                    <span className="billing-value">
                      {invoice?.customer_phone ||
                        "-"}
                    </span>

                  </div>

                  <div className="billing-row">

                    <span className="billing-label">
                      GSTIN:
                    </span>

                    <span className="billing-value">
                      {invoice?.gst_number ||
                        "N/A"}
                    </span>

                  </div>

                </div>

              </div>

            </div>

            {/* =========================================
                ORDER / TRANSACTION
            ========================================= */}

            <div className="order-tx-bar">

              <div className="bar-item">

                <strong>
                  ORDER ID:
                </strong>

                <span>
                  {invoice?.order_id ||
                    "-"}
                </span>

              </div>

              <div className="bar-item transaction-item">

                <strong>
                  TRANSACTION ID:
                </strong>

                <span>
                  {invoice?.payment_id ||
                    "-"}
                </span>

              </div>

            </div>

            {/* =========================================
                ITEMS TABLE
            ========================================= */}

            <div className="table-responsive">

              <table className="items-table">

                <colgroup>

                  <col
                    style={{
                      width: "34%",
                    }}
                  />

                  <col
                    style={{
                      width: "11%",
                    }}
                  />

                  <col
                    style={{
                      width: "11%",
                    }}
                  />

                  <col
                    style={{
                      width: "8%",
                    }}
                  />

                  <col
                    style={{
                      width: "12%",
                    }}
                  />

                  <col
                    style={{
                      width: "12%",
                    }}
                  />

                  <col
                    style={{
                      width: "12%",
                    }}
                  />

                </colgroup>

                <thead>

                  <tr>

                    <th>
                      Item
                    </th>

                    <th>
                      Price
                    </th>

                    <th>
                      Discount
                    </th>

                    <th className="center">
                      Qty
                    </th>

                    <th>
                      CGST
                    </th>

                    <th>
                      SGST
                    </th>

                    <th>
                      Net Amount
                    </th>

                  </tr>

                </thead>

                <tbody>

                  <tr>

                    {/* ITEM */}

                    <td className="item-cell">

                      <div className="item-title">
                        {invoice?.item_title ||
                          "Hacktonomy Certified AI Security Professional (HCASP)"}
                      </div>

                      <div className="item-subtitle">
                        {invoice?.item_subtitle ||
                          "Certification Exam Enrollment"}
                      </div>

                      <div className="item-tag">
                        {invoice?.item_type_tag ||
                          "CERTIFICATION"}
                      </div>

                    </td>

                    {/* PRICE (MRP, before discount) */}

                    <td className="money-cell">
                      {currencySymbol}{" "}
                      {formatAmount(
                        mrpAmount
                      )}
                    </td>

                    {/* DISCOUNT */}

                    <td className="money-cell">
                      {discountDisplay}
                    </td>

                    {/* QTY */}

                    <td className="center">
                      {quantity}
                    </td>

                    {/* CGST */}

                    <td className="tax-cell">

                      <div>
                        {currencySymbol}{" "}
                        {formatAmount(
                          cgstAmount
                        )}
                      </div>

                      <span className="tax-rate">
                        ({cgstRate})
                      </span>

                    </td>

                    {/* SGST */}

                    <td className="tax-cell">

                      <div>
                        {currencySymbol}{" "}
                        {formatAmount(
                          sgstAmount
                        )}
                      </div>

                      <span className="tax-rate">
                        ({sgstRate})
                      </span>

                    </td>

                    {/* NET AMOUNT */}

                    <td className="net-amount">
                      {currencySymbol}{" "}
                      {formatAmount(
                        finalAmount
                      )}
                    </td>

                  </tr>

                </tbody>

              </table>

            </div>

            {/* =========================================
                TOTAL
            ========================================= */}

            <div className="total-container">

              <div className="total-summary">

                <div className="summary-row">
                  <span>Price:</span>
                  <span>{currencySymbol} {formatAmount(mrpAmount)}</span>
                </div>

                {discount > 0 && (
                  <div className="summary-row discount-row">
                    <span>Discount{couponCode ? ` (${couponCode})` : ""}:</span>
                    <span>{discountDisplay}</span>
                  </div>
                )}

                <div className="summary-row">
                  <span>Taxable Amount:</span>
                  <span>{currencySymbol} {formatAmount(taxableAmount)}</span>
                </div>

                <div className="summary-row">
                  <span>CGST ({cgstRate}):</span>
                  <span>{currencySymbol} {formatAmount(cgstAmount)}</span>
                </div>

                <div className="summary-row">
                  <span>SGST ({sgstRate}):</span>
                  <span>{currencySymbol} {formatAmount(sgstAmount)}</span>
                </div>

                <div className="summary-row total-row">
                  <span>Total:</span>
                  <span className="total-amount">
                    {currencySymbol} {formatAmount(finalAmount)}
                  </span>
                </div>

              </div>

            </div>

            {/* =========================================
                FOOTER
            ========================================= */}

            <div className="bottom-footer">

              <p className="help-text">
                Questions regarding this
                invoice?

                <span>
                  {" "}
                  info@cyberfrat.com
                </span>
              </p>

            </div>

          </div>
        </main>
      </div>

      {/* =========================================
          CSS
      ========================================= */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          background: #f8fafc;
          color: #111827;
          font-family:
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Helvetica,
            Arial,
            sans-serif;
          font-size: 14px;
          line-height: 1.5;
        }

        /* =========================================
           PAGE
        ========================================= */

        .invoice-page {
          min-height: 100vh;
          background: #f8fafc;
          padding: 38px 20px 50px;
        }

        .invoice-wrapper {
          width: 100%;
          max-width: 958px;
          margin: 0 auto;
        }

        /* =========================================
           ACTION BAR
        ========================================= */

        .top-actions-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 22px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 15px;
          border-radius: 22px;
          background: #f0fdf8;
          border: 1px solid #9de9c8;
          color: #059669;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }

        .status-check {
          font-size: 15px;
          font-weight: 900;
          line-height: 1;
        }

        .action-buttons {
          display: flex;
          align-items: center;
        }

        .btn-download-pdf {
          height: 42px;
          padding: 0 24px !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          gap: 9px;
          border: none !important;
          border-radius: 9px !important;
          background: #087eb9 !important;
          color: #ffffff !important;
          font-size: 14px !important;
          font-weight: 700 !important;
          box-shadow:
            0 5px 13px
            rgba(8, 126, 185, 0.22);
        }

        .btn-download-pdf:hover {
          background: #066d9f !important;
        }

        .btn-download-pdf:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .download-loader {
          animation:
            invoice-spin 1s linear infinite;
        }

        @keyframes invoice-spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* =========================================
           INVOICE CARD
        ========================================= */

        .invoice-card {
          width: 100%;
          background: #ffffff;
          border: 1px solid #dfe6ee;
          border-radius: 8px;
          padding: 38px 41px 40px;
          box-shadow:
            0 4px 18px
            rgba(15, 23, 42, 0.045);
        }

        /* =========================================
           HEADER
        ========================================= */

        .invoice-header {
          width: 100%;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 30px;
          margin-bottom: 31px;
        }

        .header-brand {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
          padding-top: 4px;
          min-width: 0;
        }

        .brand-logo {
          width: auto;
          height: 42px;
          max-width: 54px;
          object-fit: contain;
          display: block;
        }

        .company-name {
          color: #0f172a;
          font-size: 29px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: -1.2px;
          white-space: nowrap;
        }

        .header-meta {
          text-align: right;
          padding-top: 2px;
          min-width: 250px;
        }

        .invoice-title {
          color: #0f172a;
          font-size: 23px;
          line-height: 1.2;
          font-weight: 800;
          letter-spacing: -0.4px;
          margin-bottom: 8px;
        }

        .invoice-number {
          color: #111827;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .invoice-number span {
          color: #334155;
          font-weight: 500;
        }

        .invoice-date {
          color: #475569;
          font-size: 13px;
          font-weight: 400;
        }

        /* =========================================
           SELLER / BILLING
        ========================================= */

        .parties-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 42px;
          margin-bottom: 30px;
        }

        .party-col h4 {
          margin: 0 0 11px;
          color: #111827;
          font-size: 13px;
          line-height: 1.2;
          font-weight: 800;
        }

        .seller-name {
          color: #334155;
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .seller-info {
          color: #334155;
          font-size: 13px;
          line-height: 1.55;
          margin: 1px 0;
        }

        .billing-col {
          text-align: right;
        }

        .billing-details {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 6px;
        }

        .billing-name {
          color: #111827;
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 2px;
        }

        .billing-row {
          display: flex;
          align-items: flex-start;
          justify-content: flex-end;
          gap: 12px;
          width: 100%;
          color: #334155;
          font-size: 13px;
          line-height: 1.45;
        }

        .billing-label {
          color: #64748b;
          min-width: 48px;
          font-weight: 500;
        }

        .billing-value {
          max-width: 275px;
          color: #334155;
          font-weight: 500;
          overflow-wrap: anywhere;
        }

        /* =========================================
           ORDER / TRANSACTION
        ========================================= */

        .order-tx-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 25px;
          padding: 15px 0 16px;
          margin-bottom: 27px;
          border-top: 1px solid #edf1f5;
          border-bottom: 1px solid #edf1f5;
        }

        .bar-item {
          color: #111827;
          font-size: 13px;
          line-height: 1.4;
          min-width: 0;
        }

        .bar-item strong {
          color: #111827;
          font-weight: 800;
        }

        .bar-item span {
          color: #334155;
          font-weight: 500;
          margin-left: 5px;
          font-family:
            ui-monospace,
            SFMono-Regular,
            Menlo,
            Monaco,
            Consolas,
            monospace;
          word-break: break-all;
        }

        .transaction-item {
          text-align: right;
        }

        /* =========================================
           TABLE
        ========================================= */

        .table-responsive {
          width: 100%;
          overflow-x: auto;
          margin-bottom: 24px;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 13px;
        }

        .items-table thead tr {
          background: #f1f3f5;
        }

        .items-table th {
          padding: 12px 9px;
          color: #111827;
          font-size: 12.5px;
          line-height: 1.2;
          font-weight: 800;
          text-align: right;
          white-space: nowrap;
        }

        .items-table th:first-child {
          text-align: left;
        }

        .items-table th.center,
        .items-table td.center {
          text-align: center;
        }

        .items-table td {
          padding: 17px 9px 15px;
          color: #1e293b;
          font-size: 13px;
          line-height: 1.4;
          text-align: right;
          vertical-align: top;
          border-bottom: 1px solid #edf1f5;
          overflow-wrap: anywhere;
        }

        .items-table td:first-child {
          text-align: left;
        }

        .item-title {
          color: #172033;
          font-size: 13.5px;
          font-weight: 600;
          line-height: 1.4;
          margin-bottom: 4px;
        }

        .item-subtitle {
          color: #64748b;
          font-size: 12px;
          line-height: 1.35;
          margin-bottom: 7px;
        }

        .item-tag {
          display: inline-block;
          color: #f59e0b;
          font-size: 11px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: 0.6px;
          text-transform: uppercase;
        }

        .money-cell,
        .tax-cell {
          white-space: nowrap;
        }

        .tax-rate {
          display: block;
          margin-top: 3px;
          color: #64748b;
          font-size: 11px;
          line-height: 1;
        }

        .net-amount {
          color: #111827 !important;
          font-weight: 800 !important;
          white-space: nowrap;
        }

        /* =========================================
           TOTAL
        ========================================= */

        .total-container {
          display: flex;
          justify-content: flex-end;
          padding-top: 20px;
          padding-bottom: 20px;
        }

        .total-summary {
          width: 260px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          color: #334155;
          font-size: 13.5px;
          font-weight: 500;
        }

        .discount-row span:last-child {
          color: #dc2626;
        }

        .total-row {
          margin-top: 4px;
          padding-top: 10px;
          border-top: 1px solid #e2e8f0;
          color: #0f172a;
          font-size: 17px;
          font-weight: 800;
        }

        .total-amount {
          font-size: 20px;
          font-weight: 800;
        }

        /* =========================================
           FOOTER
        ========================================= */

        .bottom-footer {
          padding-top: 21px;
          border-top: 1px dashed #dce3eb;
          text-align: center;
        }

        .help-text {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.5;
          font-weight: 400;
        }

        .help-text span {
          color: #4f6f98;
        }

        /* =========================================
           TABLET
        ========================================= */

        @media (max-width: 768px) {

          .invoice-page {
            padding: 18px 10px 35px;
          }

          .invoice-wrapper {
            max-width: 100%;
          }

          .top-actions-bar {
            align-items: stretch;
            flex-direction: column;
            margin-bottom: 15px;
          }

          .status-badge {
            align-self: flex-start;
          }

          .action-buttons {
            width: 100%;
          }

          .btn-download-pdf {
            width: 100%;
          }

          .invoice-card {
            padding: 25px 17px 28px;
            border-radius: 7px;
          }

          /* HEADER */

          .invoice-header {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 20px;
            margin-bottom: 28px;
          }

          .header-brand {
            width: 100%;
            justify-content: flex-start;
            padding-top: 0;
          }

          .brand-logo {
            height: 36px;
            max-width: 47px;
          }

          .company-name {
            font-size: 26px;
          }

          .header-meta {
            position: static;
            width: 100%;
            min-width: 0;
            text-align: left;
          }

          .invoice-title {
            font-size: 21px;
          }

          /* PARTIES */

          .parties-grid {
            grid-template-columns: 1fr;
            row-gap: 26px;
            margin-bottom: 25px;
          }

          .billing-col {
            text-align: left;
          }

          .billing-details {
            align-items: flex-start;
          }

          .billing-row {
            justify-content: flex-start;
          }

          .billing-value {
            max-width: calc(100% - 60px);
          }

          /* ORDER */

          .order-tx-bar {
            flex-direction: column;
            align-items: flex-start;
            gap: 11px;
          }

          .transaction-item {
            text-align: left;
          }

          /* TABLE */

          .table-responsive {
            overflow-x: auto;
          }

          .items-table {
            min-width: 760px;
          }

          /* TOTAL */

          .total-container {
            padding-top: 18px;
          }

          .total-box {
            font-size: 16px;
          }

          .total-amount {
            font-size: 18px;
          }
        }

        /* =========================================
           SMALL MOBILE
        ========================================= */

        @media (max-width: 480px) {

          .invoice-page {
            padding: 12px 7px 25px;
          }

          .invoice-card {
            padding: 22px 13px 25px;
          }

          .company-name {
            font-size: 23px;
          }

          .invoice-title {
            font-size: 19px;
          }

          .invoice-number,
          .invoice-date {
            font-size: 12px;
          }

          .seller-info,
          .billing-row {
            font-size: 12px;
          }

          .help-text {
            font-size: 11.5px;
          }
        }


        /* =========================================
           EXTRA MOBILE RESPONSIVE FIXES
           UI/CSS ONLY - NO LOGIC CHANGES
        ========================================= */

        @media (max-width: 768px) {
          .invoice-page {
            width: 100%;
            padding: 14px 8px 30px;
            overflow-x: hidden;
          }

          .invoice-wrapper {
            width: 100%;
            max-width: 100%;
          }

          .top-actions-bar {
            width: 100%;
            gap: 12px;
          }

          .status-badge {
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .btn-download-pdf {
            width: 100% !important;
            min-height: 44px;
          }

          .invoice-card {
            width: 100%;
            max-width: 100%;
            padding: 22px 14px 26px;
            overflow: hidden;
          }

          .invoice-header {
            width: 100%;
            gap: 18px;
          }

          .header-brand {
            width: 100%;
            min-width: 0;
          }

          .company-name {
            max-width: calc(100% - 55px);
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .header-meta {
            width: 100%;
            min-width: 0;
          }

          .invoice-number,
          .invoice-date {
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .parties-grid {
            width: 100%;
            grid-template-columns: minmax(0, 1fr);
          }

          .party-col {
            min-width: 0;
            width: 100%;
          }

          .seller-info,
          .seller-name,
          .billing-name,
          .billing-row,
          .billing-value {
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .billing-row {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: 5px 10px;
          }

          .billing-value {
            flex: 1 1 auto;
            min-width: 0;
          }

          .order-tx-bar {
            width: 100%;
          }

          .bar-item {
            width: 100%;
            min-width: 0;
          }

          .bar-item span {
            overflow-wrap: anywhere;
            word-break: break-all;
          }

          .table-responsive {
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
          }

          .items-table {
            min-width: 760px;
          }

          .total-container {
            width: 100%;
            justify-content: flex-end;
          }

          .total-summary {
            width: min(100%, 300px);
            max-width: 100%;
          }

          .summary-row {
            gap: 12px;
          }

          .summary-row span:first-child {
            min-width: 0;
          }

          .summary-row span:last-child {
            text-align: right;
            overflow-wrap: anywhere;
          }

          .bottom-footer {
            width: 100%;
          }

          .help-text {
            overflow-wrap: anywhere;
            word-break: break-word;
          }
        }

        @media (max-width: 480px) {
          .invoice-page {
            padding: 10px 5px 22px;
          }

          .top-actions-bar {
            margin-bottom: 12px;
          }

          .status-badge {
            width: 100%;
            justify-content: center;
            padding: 7px 10px;
            font-size: 12px;
          }

          .invoice-card {
            padding: 18px 11px 22px;
            border-radius: 6px;
          }

          .header-brand {
            gap: 7px;
          }

          .brand-logo {
            height: 32px;
            max-width: 42px;
          }

          .company-name {
            font-size: 21px;
            letter-spacing: -0.8px;
          }

          .invoice-title {
            font-size: 18px;
            margin-bottom: 6px;
          }

          .invoice-number,
          .invoice-date {
            font-size: 11.5px;
          }

          .party-col h4 {
            font-size: 12px;
            margin-bottom: 8px;
          }

          .seller-name,
          .billing-name {
            font-size: 13px;
          }

          .seller-info,
          .billing-row {
            font-size: 11.5px;
          }

          .order-tx-bar {
            padding: 12px 0;
            margin-bottom: 18px;
          }

          .bar-item {
            font-size: 11.5px;
          }

          .table-responsive {
            margin-left: -1px;
            width: calc(100% + 2px);
          }

          .items-table {
            min-width: 700px;
            font-size: 12px;
          }

          .items-table th {
            font-size: 11px;
            padding: 10px 7px;
          }

          .items-table td {
            font-size: 11.5px;
            padding: 13px 7px 12px;
          }

          .item-title {
            font-size: 12px;
          }

          .item-subtitle {
            font-size: 10.5px;
          }

          .item-tag,
          .tax-rate {
            font-size: 9.5px;
          }

          .total-container {
            padding-top: 14px;
            padding-bottom: 16px;
          }

          .total-summary {
            width: 100%;
          }

          .summary-row {
            font-size: 12.5px;
          }

          .total-row {
            font-size: 15px;
          }

          .total-amount {
            font-size: 17px;
          }

          .help-text {
            font-size: 11px;
          }
        }

        @media (max-width: 360px) {
          .invoice-page {
            padding-left: 3px;
            padding-right: 3px;
          }

          .invoice-card {
            padding-left: 9px;
            padding-right: 9px;
          }

          .company-name {
            font-size: 19px;
          }

          .invoice-title {
            font-size: 17px;
          }

          .items-table {
            min-width: 680px;
          }

          .items-table th,
          .items-table td {
            padding-left: 6px;
            padding-right: 6px;
          }
        }

        /* =========================================
           PRINT / PDF
        ========================================= */

        @media print {

          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          html,
          body {
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .no-print {
            display: none !important;
          }

          .invoice-page {
            width: 100%;
            min-height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }

          .invoice-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
          }

          .invoice-card {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 5mm !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }

          .invoice-header {
            flex-direction: row !important;
            align-items: flex-start !important;
            justify-content: space-between !important;
          }

          .header-brand {
            width: auto !important;
            justify-content: flex-start !important;
          }

          .header-meta {
            width: auto !important;
            min-width: 0 !important;
            text-align: right !important;
          }

          .table-responsive {
            overflow: visible !important;
          }

          .items-table {
            width: 100% !important;
            min-width: 0 !important;
          }

          .items-table th,
          .items-table td {
            font-size: 10px !important;
            padding-left: 5px !important;
            padding-right: 5px !important;
          }

          .item-title {
            font-size: 10.5px !important;
          }

          .item-subtitle,
          .item-tag,
          .tax-rate {
            font-size: 9px !important;
          }

          .invoice-title {
            font-size: 18px !important;
          }

          .company-name {
            font-size: 24px !important;
          }

          .brand-logo {
            height: 34px !important;
          }

          .parties-grid {
            margin-bottom: 20px !important;
          }

          .order-tx-bar {
            margin-bottom: 18px !important;
          }

          .total-container {
            padding-top: 12px !important;
            padding-bottom: 12px !important;
          }

          .total-amount {
            font-size: 17px !important;
          }

          .bottom-footer {
            padding-top: 14px !important;
          }
        }

      `}</style>
    </>
  );
};

export default Invoice;